import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, ShieldCheck, Sparkles, Upload } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api-client';
import { useToast } from '@/components/ui/use-toast';
import { FILE_LIMITS } from '@parecer/shared';

interface CreateContractResponse {
  contractId: string;
  versionId: string;
  uploadUrl: string;
}

export function ContratoNovo() {
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<'idle' | 'creating' | 'uploading' | 'analyzing'>('idle');
  const navigate = useNavigate();
  const { toast } = useToast();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file || !title) return;

    if (file.size > FILE_LIMITS.MAX_CONTRACT_SIZE_BYTES) {
      toast({ variant: 'danger', title: 'Arquivo muito grande', description: 'Máximo 20MB.' });
      return;
    }
    if (!FILE_LIMITS.ALLOWED_CONTRACT_MIME_TYPES.includes(file.type as 'application/pdf' | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
      toast({ variant: 'danger', title: 'Tipo não suportado', description: 'Use PDF ou DOCX.' });
      return;
    }

    setProgress('creating');
    try {
      const r = await api.post<CreateContractResponse>('/contracts', {
        title,
        fileName: file.name,
        mimeType: file.type,
        fileSize: file.size,
      });

      setProgress('uploading');
      const upload = await fetch(r.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type, 'x-amz-server-side-encryption': 'aws:kms' },
        body: file,
      });
      if (!upload.ok) throw new Error('Falha no upload S3');

      setProgress('analyzing');
      // Trigger analysis. Don't await full response — UI shows analyzing state on detail page.
      void api.post(`/contracts/${r.contractId}/analyze`);

      toast({ variant: 'success', title: 'Contrato enviado', description: 'Análise IA em andamento.' });
      navigate(`/app/contratos/${r.contractId}`);
    } catch (err) {
      setProgress('idle');
      toast({
        variant: 'danger',
        title: 'Falha ao enviar',
        description: err instanceof Error ? err.message : 'Erro inesperado.',
      });
    }
  }

  return (
    <AppShell role="users">
      <div className="max-w-2xl">
        <h1 className="font-serif text-display-lg">Novo contrato</h1>
        <p className="text-muted-foreground mt-2">PDF ou DOCX, até 20MB. A IA analisa em segundos.</p>

        {/* Confidentiality reassurance — explicit before upload, not buried in FAQ */}
        <Card className="mt-6 border-success/30 bg-success/[0.03]">
          <CardContent className="pt-5 space-y-2 text-sm">
            <p className="flex items-start gap-2 font-medium text-foreground">
              <Lock className="h-4 w-4 text-success mt-0.5 flex-none" />
              Seu contrato é privado por padrão.
            </p>
            <ul className="space-y-1 text-muted-foreground text-[13px] pl-6">
              <li className="flex gap-2">
                <ShieldCheck className="h-3.5 w-3.5 text-success mt-0.5 flex-none" />
                <span>
                  <strong>Criptografado em repouso (S3 + KMS)</strong> e em trânsito (TLS).
                  Nenhum advogado vê até você escolhê-lo.
                </span>
              </li>
              <li className="flex gap-2">
                <Sparkles className="h-3.5 w-3.5 text-success mt-0.5 flex-none" />
                <span>
                  A análise IA roda em <strong>ambiente segregado da Anthropic via AWS Bedrock</strong>{' '}
                  — seu documento NÃO é usado para treinar modelos.
                </span>
              </li>
              <li className="text-[12px]">
                Saiba mais sobre segurança no nosso{' '}
                <Link to="/faq" className="text-accent hover:underline">FAQ</Link>.
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">Detalhes</CardTitle>
            <CardDescription>Dê um título descritivo pro contrato.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Título</Label>
                <Input
                  id="title"
                  required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex: NDA com fornecedor X"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="file">Arquivo</Label>
                <label
                  htmlFor="file"
                  className="mt-1.5 flex flex-col items-center justify-center gap-2 cursor-pointer rounded-md border-2 border-dashed border-border bg-surface/40 px-6 py-12 hover:border-accent/40 transition-colors"
                >
                  <Upload className="h-6 w-6 text-muted-foreground" />
                  {file ? (
                    <p className="text-sm">
                      <span className="font-medium">{file.name}</span>{' '}
                      <span className="text-muted-foreground">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">Clique para selecionar PDF ou DOCX</p>
                  )}
                </label>
                <input
                  id="file"
                  type="file"
                  accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </div>
              <Button
                type="submit"
                variant="accent"
                className="w-full"
                disabled={!file || !title}
                loading={progress !== 'idle'}
              >
                {progress === 'creating' && 'Criando…'}
                {progress === 'uploading' && 'Enviando…'}
                {progress === 'analyzing' && 'Iniciando análise…'}
                {progress === 'idle' && 'Enviar e analisar'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
